import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, Subscription } from "../lib/api";
import { money, shortDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { OptionBadge } from "./OptionBadge";
import { StatusBadge } from "./StatusBadge";

interface ClubSummary { tiers: { id: string; name: string; price_pence: number }[]; membership: { tier: string; status: string; next_case_due?: string | null; custom_price_pence?: number } | null }
interface Mandate { status?: string | null }

const cadence = (s: Subscription) => `every ${s.interval > 1 ? `${s.interval} ${s.interval_unit.replace("ly", "s")}` : s.interval_unit.replace("ly", "")}`;

export function ProfileCard() {
  const { user } = useAuth();
  const { data: subs } = useQuery<Subscription[]>({ queryKey: ["subscriptions"], queryFn: () => api.get("/subscriptions").then((r) => r.data) });
  const { data: club } = useQuery<ClubSummary>({ queryKey: ["club"], queryFn: () => api.get("/club").then((r) => r.data) });
  const { data: mandate } = useQuery<Mandate>({ queryKey: ["mandate"], queryFn: () => api.get("/mandates/current").then((r) => r.data) });
  if (!user || typeof user !== "object") return null;
  const cellar = subs?.find((s) => (s.status === "active" || s.status === "paused") && !s.club);
  const m = club?.membership && club.membership.status !== "cancelled" ? club.membership : null;
  const tier = club?.tiers.find((t) => t.id === m?.tier);
  const u = user as any;
  return (
    <section id="profile" className="scroll-mt-20 border border-border bg-[var(--parchment)] p-6 rise" data-testid="profile-card">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="eyebrow">Your profile</div>
          <div className="mt-2 font-serif text-2xl" data-testid="profile-name">{user.name}</div>
          <dl className="mt-2 space-y-0.5 text-xs text-neutral-600">
            <div className="flex gap-2"><dt className="w-24 uppercase tracking-wider text-neutral-400">Login</dt><dd data-testid="profile-email">{user.email}</dd></div>
            {u.created_at && <div className="flex gap-2"><dt className="w-24 uppercase tracking-wider text-neutral-400">Member since</dt><dd>{shortDate(u.created_at)}</dd></div>}
            <div className="flex gap-2"><dt className="w-24 uppercase tracking-wider text-neutral-400">Direct Debit</dt><dd className="flex items-center gap-2" data-testid="profile-mandate">{mandate?.status && mandate.status !== "none" ? <StatusBadge status={mandate.status} testId="profile-mandate-status" /> : <Link to="/cellar?tab=topup" className="underline underline-offset-4">not set up — connect your bank</Link>}</dd></div>
          </dl>
        </div>
        <div className="flex flex-col gap-2 text-sm" data-testid="profile-plans">
          <div className="eyebrow">You're on right now</div>
          {subs === undefined || club === undefined ? <span className="text-neutral-400">…</span> : (
            <>
              {cellar && <Link to="/cellar?tab=plan" className="flex items-center gap-3" data-testid="profile-plan-cellar"><OptionBadge kind="cellar" text={`Cellar Plan · ${money(cellar.amount_pence)} ${cadence(cellar)}`} /><StatusBadge status={cellar.status} testId="profile-plan-cellar-status" /></Link>}
              {m && <Link to="/club" className="flex items-center gap-3" data-testid="profile-plan-club"><OptionBadge kind="club" text={`Wine Club · ${tier?.name || "Bespoke plan"}`} /><StatusBadge status={m.status} testId="profile-plan-club-status" />{m.next_case_due && <span className="text-xs text-neutral-500">next case {shortDate(m.next_case_due)}</span>}</Link>}
              {!cellar && !m && <span className="inline-block border border-neutral-300 bg-neutral-100 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500" data-testid="profile-plan-none">No plan yet</span>}
            </>
          )}
          <div className="mt-2 flex flex-wrap gap-4 text-xs">
            <Link to="/cellar?tab=plan" className="underline underline-offset-4" data-testid="profile-manage-plans">Manage plans</Link>
            <Link to="/club" className="underline underline-offset-4">Wine Club</Link>
            <Link to="/cellar?tab=password" className="underline underline-offset-4" data-testid="profile-change-password">Change password</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
